/**
 * D-Brief — the exception brief, and a question box over the evidence.
 *
 * One page. Three things that will degrade capacity in the next thirty days,
 * each with its confidence, its cost in closure minutes and remote-stand
 * penalties, and the decision to make this week and who owns it. Under it,
 * a question box: "why did we close runway 17/35 for six hours last Tuesday
 * and what did it cost?" — answered from the closure record, the inspection,
 * the work order, its photographs and the clause the rule carries.
 *
 * Built on the intelligence report's evidence base (`gather.ts`) plus the
 * platform's closures and its capacity model. Computed when opened and held
 * for the day; the change feed refreshes it when the estate moves.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import type { ClosureCost } from "../../domain/capacity";
import type { Closure } from "../../domain/closures";
import {
  answerFromRecords,
  briefWindowFor,
  buildBrief,
  capacityCandidates,
  costLinesFrom,
  parseEvidenceQuestion,
  zoneOf,
  type Attachment,
  type Brief,
  type BriefItem,
  type EvidenceAnswer,
} from "../../domain/dbrief";
import type { Inspection } from "../../domain/inspections";
import { Button, Card, ErrorState, Input, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useIntelligence, type Gathered } from "../intelligence/gather";
import s from "./DBrief.module.css";
import { t, tn } from "../../i18n";

interface BriefData {
  brief: Brief;
  closures: Closure[];
  inspections: Inspection[];
  /* what the brief was built from, so it can be rebuilt in the reader's language without fetching again */
  now: Date;
  closureCosts: Record<string, ClosureCost | null>;
}

function useBrief(gathered: Gathered | undefined) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  const select = useCallback(
    (d: BriefData): BriefData =>
      gathered
        ? {
            ...d,
            brief: buildBrief(gathered.report, {
              now: d.now,
              closures: d.closures,
              closureCosts: d.closureCosts,
              workOrders: gathered.inputs.workOrders,
            }),
          }
        : d,
    [gathered],
  );
  return useQuery({
    queryKey: ["d-brief", siteId, gathered?.report.generatedAt ?? ""],
    select,
    enabled: !!gathered,
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<BriefData> => {
      const g = gathered!;
      const now = new Date();
      const since = new Date(now.getTime() - 90 * 86_400_000).toISOString();
      const [closures, inspections] = await Promise.all([
        source.listClosures(siteId, since).catch(() => [] as Closure[]),
        source.listInspections({ siteId }).catch(() => [] as Inspection[]),
      ]);
      const zones = [
        ...new Set(
          capacityCandidates(g.report)
            .slice(0, 3)
            .map(zoneOf)
            .filter((z): z is string => !!z),
        ),
      ];
      const closureCosts: Record<string, ClosureCost | null> = {};
      await Promise.all(
        zones.map(async (zone) => {
          const w = briefWindowFor(zone, closures, now);
          closureCosts[zone] = await source
            .closureCost({ siteId, positionCode: zone, from: w.from, to: w.to })
            .catch(() => null);
        }),
      );
      return {
        brief: buildBrief(g.report, { now, closures, closureCosts, workOrders: g.inputs.workOrders }),
        closures,
        inspections,
        now,
        closureCosts,
      };
    },
  });
}

export function DBriefPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const brief = useBrief(gathered.data);
  const pending = gathered.isPending || (gathered.data && brief.isPending);
  return (
    <Page
      eyebrow={`${session.site.code} · ${t("Executive")}`}
      title={
        <>
          D-Brief <span className={s.date}>{brief.data?.brief.date ?? ""}</span>
        </>
      }
      actions={
        <>
          {brief.data && (
            <span className={s.stamp}>
              {t("as of {date} UTC", { date: brief.data.brief.generatedAt.replace("T", " ").slice(0, 16) })}
            </span>
          )}
          <Button variant="ghost" onClick={() => void brief.refetch()} disabled={!brief.data}>
            {t("Regenerate")}
          </Button>
          <Button variant="primary" onClick={() => window.print()} disabled={!brief.data}>
            {t("Print")}
          </Button>
        </>
      }
    >
      <p className={s.lead}>
        {t(
          "The top three risks to capacity in the next 30 days, what each costs, and this week's decision. Then ask anything.",
        )}
      </p>
      {pending && <Skeleton rows={6} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {brief.isError && <ErrorState error={brief.error} retry={() => void brief.refetch()} />}
      {brief.data && gathered.data && (
        <div className={`${s.brief} d-brief`}>
          <BriefItems brief={brief.data.brief} />
          <AskTheEvidence
            gathered={gathered.data}
            closures={brief.data.closures}
            inspections={brief.data.inspections}
          />
        </div>
      )}
    </Page>
  );
}

function BriefItems({ brief }: { brief: Brief }) {
  return (
    <Card
      title="What will degrade capacity in the next 30 days"
      sub={`${t("{n} of {findings} weighed", {
        n: brief.items.length,
        findings: tn(brief.considered, "{n} capacity finding", "{n} capacity findings"),
      })} · ${brief.siteCode}`}
    >
      {brief.items.length === 0 ? (
        <p className={s.note}>
          {t(
            "Nothing affects capacity in the next 30 days. See the intelligence report for more.",
          )}
        </p>
      ) : (
        <ol className={s.items}>
          {brief.items.map((it) => (
            <BriefRow key={it.findingId} it={it} />
          ))}
        </ol>
      )}
      {brief.notes.length > 0 && (
        <ul className={s.notes}>
          {brief.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function BriefRow({ it }: { it: BriefItem }) {
  return (
    <li className={s.item} data-testid={`brief-${it.rank}`}>
      <div className={s.rank}>{it.rank}</div>
      <div className={s.body}>
        <div className={s.head}>
          <h3 className={s.headline}>{it.headline}</h3>
          {it.zone && <span className={s.zone}>{it.zone}</span>}
          <span className={s.when}>{it.horizon}</span>
        </div>
        <div className={s.cells}>
          <div className={s.cell}>
            <div className={s.cellTitle}>{t("Confidence")}</div>
            <div className={`${s.conf} ${s[it.confidence.level]}`}>
              {t(it.confidence.level)}
              {it.confidence.pct != null ? ` · ${it.confidence.pct}%` : ""}
            </div>
            <div className={s.cellSub}>{it.confidence.why}</div>
          </div>
          <div className={s.cell}>
            <div className={s.cellTitle}>{t("Closure minutes")}</div>
            <div className={s.big}>{it.closureMinutes.value != null ? `${it.closureMinutes.value} min` : "—"}</div>
            <div className={s.cellSub}>{it.closureMinutes.basis}</div>
          </div>
          <div className={s.cell}>
            <div className={s.cellTitle}>{t("Remote-stand penalty")}</div>
            <div className={s.big}>
              {it.remoteStand.bussed != null ? t("{n} bussed", { n: it.remoteStand.bussed }) : "—"}
              {it.remoteStand.stranded ? ` · ${t("{n} stranded", { n: it.remoteStand.stranded })}` : ""}
            </div>
            <div className={s.cellSub}>{it.remoteStand.basis}</div>
          </div>
          <div className={`${s.cell} ${s.decision}`}>
            <div className={s.cellTitle}>{t("Decision this week")}</div>
            <div className={s.what}>{it.decision.what}</div>
            <div className={s.cellSub}>
              <b className={it.decision.ownerKnown ? s.owner : s.nobody}>{it.decision.owner}</b> · {it.decision.by}
            </div>
          </div>
        </div>
        <div className={s.refs}>
          {it.evidence.map((e) => (
            <span key={`${e.kind}:${e.ref}`} className={s.ref} title={e.detail}>
              {e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}
            </span>
          ))}
          <Link className={s.more} to="/intelligence-report">
            {t("full finding →")}
          </Link>
        </div>
      </div>
    </li>
  );
}

/* ── ask the evidence ──────────────────────────────────────────────────── */

/* the question parser reads English, so the examples are asked in English whatever the screen's language */
const EXAMPLES = [
  "Why did we close runway 17/35 for six hours last Tuesday and what did it cost?",
  "Why was taxiway B closed last Tuesday?",
  "What closed stand A12 this week?",
];

function AskTheEvidence({
  gathered,
  closures,
  inspections,
}: {
  gathered: Gathered;
  closures: Closure[];
  inspections: Inspection[];
}) {
  const session = useSession();
  const source = session.source;
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<EvidenceAnswer | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async (q: string) => {
    const text = q.trim();
    if (!text) return;
    setBusy(true);
    setFallback(null);
    const now = new Date();
    const known = [
      ...new Set(
        [...closures.map((c) => c.zoneCode), ...gathered.inputs.workOrders.map((w) => w.zoneCode)].filter(
          (z): z is string => !!z,
        ),
      ),
    ];
    const parsed = parseEvidenceQuestion(text, now, known);
    const base = answerFromRecords(text, parsed, {
      now,
      closures,
      workOrders: gathered.inputs.workOrders,
      inspections,
      alerts: gathered.inputs.alerts,
      explanations: gathered.inputs.explanations,
      rules: gathered.inputs.rules,
    });
    setAnswer(base);
    if (!base.found) {
      if (!parsed.zone) {
        /* not a closure question: the platform's own assistant, which refuses rather than guesses */
        try {
          const reply = await source.askAssistant({ question: text, siteId: session.site.id });
          setFallback(reply.answer.text);
        } catch {
          setFallback(null);
        }
      }
      setBusy(false);
      return;
    }
    /* the capacity model on the actual window, and the photographs on the work */
    const [cc, evidence] = await Promise.all([
      base.costWindow
        ? source
            .closureCost({
              siteId: session.site.id,
              positionCode: base.costWindow.positionCode,
              from: base.costWindow.from,
              to: base.costWindow.to,
            })
            .catch(() => null)
        : Promise.resolve(null),
      Promise.all(
        base.workOrderIds.map((id) =>
          source
            .listEvidence("work_order", id)
            .then((ev) => ({ id, ev }))
            .catch(() => ({ id, ev: [] })),
        ),
      ),
    ]);
    const photos: Attachment[] = evidence.flatMap(({ id, ev }) =>
      ev
        .filter((e) => e.objectId && (e.contentType ?? "").startsWith("image/"))
        .map((e) => ({
          kind: "photo" as const,
          ref: e.phase ?? "photo",
          label: e.phase ? t("{phase} photograph", { phase: e.phase }) : t("photo photograph"),
          detail: e.createdAt.slice(0, 16).replace("T", " "),
          objectId: e.objectId,
          of: base.attachments.find((a) => a.href === `/work-orders/${id}`)?.ref,
        })),
    );
    setAnswer({ ...base, cost: [...base.cost, ...costLinesFrom(cc)], attachments: [...base.attachments, ...photos] });
    setBusy(false);
  };

  return (
    <Card title="Ask the evidence" sub="Ask about a closure, zone or day">
      <form
        className={s.ask}
        onSubmit={(e) => {
          e.preventDefault();
          void ask(question);
        }}
      >
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={EXAMPLES[0]}
          aria-label={t("Ask the evidence")}
        />
        <Button type="submit" variant="primary" disabled={busy || !question.trim()}>
          {busy ? t("Looking…") : t("Ask")}
        </Button>
      </form>
      <div className={s.examples}>
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            className={s.example}
            onClick={() => {
              setQuestion(ex);
              void ask(ex);
            }}
          >
            {ex}
          </button>
        ))}
      </div>
      {answer && !answer.found && (
        <div className={s.answer} data-testid="evidence-answer">
          <p className={s.notFound}>{answer.notFound}</p>
          {fallback && (
            <>
              <div className={s.secTitle}>{t("The platform's assistant says")}</div>
              <p>{fallback}</p>
            </>
          )}
        </div>
      )}
      {answer && answer.found && <Answer a={answer} />}
    </Card>
  );
}

function Answer({ a }: { a: EvidenceAnswer }) {
  const groups: Attachment["kind"][] = ["closure", "inspection", "work_order", "photo", "clause", "alert"];
  const labels: Record<Attachment["kind"], string> = {
    closure: "Closure records",
    inspection: "Inspection records",
    work_order: "Work orders",
    photo: "Photographs",
    clause: "Clause references",
    alert: "Alerts",
  };
  return (
    <div className={s.answer} data-testid="evidence-answer">
      <h3 className={s.answerHead}>{a.headline}</h3>
      <div className={s.answerCols}>
        <div>
          <div className={s.secTitle}>{t("What happened")}</div>
          {a.happened.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
        <div>
          <div className={s.secTitle}>{t("Why")}</div>
          {a.why.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
        <div>
          <div className={s.secTitle}>{t("What it cost")}</div>
          {a.cost.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
      </div>
      <div className={s.secTitle}>{t("Attached")}</div>
      <div className={s.attached}>
        {groups
          .filter((k) => a.attachments.some((x) => x.kind === k))
          .map((k) => (
            <div key={k} className={s.attachGroup}>
              <div className={s.attachKind}>{t(labels[k])}</div>
              {k === "photo" ? (
                <div className={s.photos}>
                  {a.attachments
                    .filter((x) => x.kind === "photo")
                    .map((x) => (
                      <Photo key={x.objectId} att={x} />
                    ))}
                </div>
              ) : (
                a.attachments
                  .filter((x) => x.kind === k)
                  .map((x) => (
                    <div key={`${x.kind}:${x.ref}:${x.detail ?? ""}`} className={s.attachRow}>
                      <span className={s.ref}>{x.href ? <Link to={x.href}>{x.ref}</Link> : x.ref}</span>
                      <span>{x.label}</span>
                      {x.detail && <span className={s.attachDetail}>{x.detail}</span>}
                    </div>
                  ))
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/** A photograph from the evidence store, fetched with the session's credentials. */
function Photo({ att }: { att: Attachment }) {
  const session = useSession();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    let object: string | null = null;
    if (!att.objectId) return;
    session.source
      .downloadFile(att.objectId)
      .then(async (blob) => {
        if (!live) return;
        /* an object URL where the browser gives one; a test runner's URL throws on its blobs, so inline it there */
        let objectUrl: string | null = null;
        try {
          objectUrl = URL.createObjectURL(blob);
        } catch {
          objectUrl = null;
        }
        if (objectUrl) {
          object = objectUrl;
          setUrl(objectUrl);
        } else {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result));
            r.onerror = () => reject(r.error ?? new Error("could not read the file"));
            r.readAsDataURL(blob);
          });
          if (live) setUrl(dataUrl);
        }
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (object && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(object);
    };
  }, [att.objectId, session.source]);
  return (
    <figure className={s.photo}>
      {url ? (
        <img src={url} alt={att.label} />
      ) : (
        <div className={s.photoBox}>{failed ? t("could not be fetched") : t("fetching…")}</div>
      )}
      <figcaption>
        {att.label}
        {att.of ? ` · ${att.of}` : ""}
        {att.detail ? ` · ${att.detail}` : ""}
      </figcaption>
    </figure>
  );
}
